import {
  AccountInternalMap,
  AccountReviewVersion,
  CrbAccountType,
  CrbInternalId,
  DispensaryAccountReviewV2,
  DispensaryAccountReviewWithDataV2,
  DueDiligenceStatus,
  MinifiedDispensary,
  MuoAccountReviewV2,
  MUOBankResponse,
  ReviewTargets
} from '@gcv/shared';
import { DispensariesApi } from 'api';
import { action, runInAction } from 'mobx';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { FilterListChild } from 'ui';
import { Model, model } from './model';

class Actions {
  constructor(private model: Model) {}

  dispensariesApi = new DispensariesApi();

  appViewStore = getAppViewStateStore();
  fiBankStore = getFiBankStore();
  fiDispStore = getFiDispensaryStore();

  getAccountType = (account: CrbInternalId) => {
    let accountType = '';

    switch (account.type) {
      case CrbAccountType.CertificateOfDeposit:
        accountType = 'Certificate of Deposit';
        break;
      case CrbAccountType.Checking:
        accountType = 'Checking';
        break;
      case CrbAccountType.MoneyMarket:
        accountType = 'Money Market';
        break;
      case CrbAccountType.RoyaltyAccount:
        accountType = 'Royalty Account';
        break;
      case CrbAccountType.Savings:
        accountType = 'Savings';
        break;
    }

    return accountType;
  };

  getSalesLocationFilterIndexed = action((key: string, children: MinifiedDispensary[]) => {
    this.model.filterArray[key].salesLocationFilter.push(
      ...children.map((c) => {
        return {
          label: c.name,
          selected: true,
          value: c.id
        };
      })
    );
  });

  getSalesLocationFilter = action((dispensary: MUOBankResponse | MinifiedDispensary) => {
    this.model.salesLocationFilter.push({
      label: dispensary.name,
      selected: true,
      value: dispensary.id
    });
  });

  getAccountNumbersFilterIndexed = action((key: string, accounts: AccountInternalMap) => {
    // if we already populated shared account numbers then skip
    if (
      this.model.filterArray[key] &&
      this.model.filterArray[key].accountNumberFilter &&
      this.model.filterArray[key].accountNumberFilter.some((n) => Object.keys(accounts).includes(n.value))
    ) {
      return;
    }

    const accountNumbers = Object.keys(accounts).map((key) => {
      const account = accounts[key];

      return {
        label: `${account.name ? account.name : this.getAccountType(account)} *${key.slice(-4)}`,
        selected: true,
        value: key
      };
    });

    this.model.filterArray[key].accountNumberFilter.push(...accountNumbers);
  });

  getAccountNumbersFilter = action(
    (
      dispensary: MinifiedDispensary | MUOBankResponse,
      accounts: AccountInternalMap,
      review: MuoAccountReviewV2 | DispensaryAccountReviewV2
    ) => {
      // if we already populated shared account numbers then skip
      if (this.model.accountNumberFilter.some((n) => Object.keys(accounts).includes(n.value))) {
        return;
      }

      const accountNumbers: FilterListChild[] = [];
      for (const key of Object.keys(accounts)) {
        const account = accounts[key];
        let count = 0;

        if (!review) {
          continue;
        }

        if ((review as MuoAccountReviewV2).targets) {
          for (const location_id of Object.keys((review as MuoAccountReviewV2).targets)) {
            if (
              (review as MuoAccountReviewV2).targets[location_id] &&
              (review as MuoAccountReviewV2).targets[location_id].account_numbers?.includes(key)
            ) {
              count++;
            }
          }
        } else if ((review as DispensaryAccountReviewV2).account_numbers) {
          if ((review as DispensaryAccountReviewV2).account_numbers.includes(key)) {
            count++;
          }
        }

        if (count > 0) {
          accountNumbers.push({
            label: `${account.name ? account.name : this.getAccountType(account)} *${key.slice(-4)}`,
            parentValue: dispensary.id,
            selected: true,
            value: key
          });
        }
      }
      this.model.accountNumberFilter.push(...accountNumbers);
    }
  );

  getAccountsfilterIndexed = action(
    (key: string, dispensary: MinifiedDispensary | MUOBankResponse, accounts: AccountInternalMap) => {
      this.model.filterArray[key].accountsFilter.push({
        label: dispensary.name,
        value: dispensary.id,
        children: Object.keys(accounts).reduce((acc, curr) => {
          const account = accounts[curr];

          acc.push({
            label: `${account.name ? account.name : this.getAccountType(account)} *${curr.slice(-4)}`,
            parentValue: dispensary.id,
            selected: true,
            value: curr
          });

          return acc;
        }, [] as FilterListChild[])
      });
    }
  );

  getAccountsFilter = action(
    (dispensary: MinifiedDispensary | MUOBankResponse, accounts: AccountInternalMap) => {
      this.model.accountsFilter.push({
        label: dispensary.name,
        value: dispensary.id,
        selected: true,
        children: Object.keys(accounts).reduce((acc, curr) => {
          const account = accounts[curr];
          if (Object.keys(accounts).length > 1) {
            acc.push({
              label: `${account.name ? account.name : this.getAccountType(account)} *${curr.slice(-4)}`,
              parentValue: dispensary.id,
              selected: true,
              value: curr
            });
          }
          return acc;
        }, [] as FilterListChild[])
      });
    }
  );

  private isMuoAccountReviewV2 = (review: DispensaryAccountReviewWithDataV2 | MuoAccountReviewV2) => {
    const candidate = review as unknown as { targets?: ReviewTargets };
    return !!candidate.targets;
  };

  private filterBankAccountsUsingMUOReview = (accounts: AccountInternalMap, review: MuoAccountReviewV2) => {
    const whitelist = Object.values(review.targets)
      .filter((v) => !!v.account_numbers?.length)
      .map((v) => v.account_numbers ?? [])
      .flat();

    const includedAccounts = {} as AccountInternalMap;

    Object.keys(accounts).forEach((k) => {
      if (whitelist.includes(k)) {
        includedAccounts[k] = accounts[k];
      }
    });

    return includedAccounts;
  };

  private filterBankAccounts = (accounts: AccountInternalMap, review: DispensaryAccountReviewWithDataV2) => {
    const whitelist = review.account_numbers
      .filter((v) => !!v?.length)
      .map((v) => v ?? [])
      .flat();

    const includedAccounts = {} as AccountInternalMap;

    Object.keys(accounts).forEach((k) => {
      if (whitelist.includes(k)) {
        includedAccounts[k] = accounts[k];
      }
    });

    return includedAccounts;
  };

  getActivityFilters = action(
    async (crbId: string, review?: DispensaryAccountReviewWithDataV2 | MuoAccountReviewV2, key?: string) => {
      this.model.loadingActivityFilters = true;

      const metadata = await this.dispensariesApi.getMetaData(this.fiBankStore.bank.id, crbId);
      let accounts = metadata?.value?.accounts ?? {};

      if (review && this.isMuoAccountReviewV2(review)) {
        const muoReview = review as MuoAccountReviewV2;
        accounts = this.filterBankAccountsUsingMUOReview(accounts, muoReview);
      } else if (review) {
        const dispReview = review as DispensaryAccountReviewWithDataV2;
        accounts = this.filterBankAccounts(accounts, dispReview);
      }

      const dispensary =
        this.fiDispStore.bankMuos.find((d) => d.id === crbId) ??
        this.fiDispStore.dispensaries.find((d) => d.id === crbId) ??
        ({} as MinifiedDispensary);
      const children = this.fiDispStore.dispensaries
        .filter(
          (subdisp) =>
            subdisp.muo_id === crbId &&
            subdisp.assigned_onboarding_template.status === DueDiligenceStatus.BANK_APPROVED
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      if (key !== undefined) {
        this.getAccountNumbersFilterIndexed(key, accounts);
        this.getAccountsfilterIndexed(key, dispensary, accounts);
        this.getSalesLocationFilterIndexed(key, children);
      } else {
        this.getAccountNumbersFilter(dispensary, accounts, review as unknown as MuoAccountReviewV2);
        this.getAccountsFilter(dispensary, accounts);
        this.getSalesLocationFilter(dispensary);
      }

      if (children.length) {
        for (const child of children) {
          if (review && review.version === AccountReviewVersion.Two) {
            if (Object.keys((review as unknown as MuoAccountReviewV2).targets).includes(child.id)) {
              await this.getActivityFilters(child.id, review);
            }
          }
        }
      }

      runInAction(() => {
        this.model.loadingActivityFilters = false;
      });
    }
  );

  updateAppViewStore = action(() => {
    this.appViewStore.accountNumberLocationFilters.selectedAccountNumbers = this.model.selectedAccountNumbers;
    this.appViewStore.accountNumberLocationFilters.selectedLocations = this.model.selectedLocations;
  });

  clearActivityFilters = action(() => {
    this.model.clearFilters();
    this.appViewStore.accountNumberLocationFilters = {
      selectedAccountNumbers: [],
      selectedLocations: []
    };
  });

  selectFiltersIndexed = action((key: string, selected: FilterListChild[]) => {
    // clear existing selections
    for (const accountNumber of this.model.filterArray[key].accountNumberFilter) {
      accountNumber.selected = false;
    }

    for (const location of this.model.filterArray[key].salesLocationFilter) {
      location.selected = false;
    }

    // select new filters
    for (const filter of selected) {
      const account = this.model.filterArray[key].accountNumberFilter.find((a) => a.value === filter.value);
      const location = this.model.filterArray[key].salesLocationFilter.find(
        (a) => a.value === filter.value || a.value === filter.parentValue
      );

      if (account) {
        account.selected = true;
      }

      if (location) {
        location.selected = true;
      }
    }

    this.appViewStore.accountNumberLocationFiltersIndexed[key] = {
      selectedAccountNumbers: [],
      selectedLocations: []
    };

    this.appViewStore.accountNumberLocationFiltersIndexed[key].selectedAccountNumbers =
      this.model.filterArray[key].accountNumberFilter.filter((accountNumber) => accountNumber.selected);
    this.appViewStore.accountNumberLocationFiltersIndexed[key].selectedLocations = this.model.filterArray[
      key
    ].salesLocationFilter.filter((location) => location.selected);
  });

  selectFilters = action((selected: FilterListChild[]) => {
    // clear existing selections
    for (const accountNumber of this.model.accountNumberFilter) {
      accountNumber.selected = false;
    }

    for (const location of this.model.salesLocationFilter) {
      location.selected = false;
    }

    // select new filters
    for (const filter of selected) {
      const account = this.model.accountNumberFilter.find((a) => a.value === filter.value);
      const location = this.model.salesLocationFilter.find(
        (a) => a.value === filter.value || a.value === filter.parentValue
      );

      if (account) {
        account.selected = true;
      }

      if (location) {
        location.selected = true;
      }
    }

    this.appViewStore.accountNumberLocationFilters.selectedAccountNumbers = this.model.selectedAccountNumbers;
    this.appViewStore.accountNumberLocationFilters.selectedLocations = this.model.selectedLocations;
  });

  setFetchData = action((shouldFetch: boolean) => {
    this.model.fetchData = shouldFetch;
  });

  setPreviousFilters = action((filters: FilterListChild[]) => {
    this.model.previousFilters = JSON.parse(JSON.stringify(filters));
  });
}

export const actions = new Actions(model);
